import React, { useState, useEffect, useContext, useCallback } from 'react'
import { Flex } from 'reflexbox'

import { toast } from 'react-toastify'
import { ThemeContext } from 'styled-components'
import { Formik, Form } from 'formik'
import { useHistory } from 'react-router-dom'

import Breadcrumb from 'components/Breadcrumb'
import Button from 'components/Button'
import DataListingTable from 'components/DataListingTable'
import TextButton from 'components/TextButton'
import { Title } from 'components/Typography'
import { Delete } from 'components/Icons'
import { TextField, SelectField } from 'components/Form'

import DeleteGiftCardModal from 'containers/DeleteGiftCardModal'

import { getCardTypes } from 'services/giftCards'
import { formatCurrency } from 'utils/formatters'
import { translateGiftCardType } from 'utils/translators'

const columns = [
  {
    accessor: 'description',
    Header: 'Cartão',
    id: 'description',
    disableSortBy: true,
  },
  {
    accessor: 'value',
    Header: 'Descrição',
  },
  {
    accessor: 'card_type',
    Header: 'Tipo de cartão',
  },
  {
    accessor: 'actions',
    Header: '',
    disableSortBy: true,
  },
]

const breadcrumbPath = [
  {
    label: 'Cartões presente',
    link: '/staff/prepaid-partners/gift-card',
  },
  {
    label: 'Lista de cartões',
  },
]

const GiftCardList = () => {
  const [tableData, setTableData] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [sortBy, setSortBy] = useState(null)
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalPages: 1,
    totalRecords: 0,
  })
  const [filters, setFilters] = useState({})
  const [cardToDelete, setCardToDelete] = useState(null)

  const theme = useContext(ThemeContext)
  const history = useHistory()

  const handleTableDataFetch = useCallback(
    ({ sortBy, currentPage, pageSize }) => {
      setSortBy(sortBy)
      setPagination({ ...pagination, currentPage, pageSize })
    },
    [sortBy, pagination.currentPage, pagination.pageSize],
  )

  const renderTableActions = useCallback(
    card => (
      <Flex justifyContent='flex-end'>
        <TextButton
          variant='custom'
          color={theme.colors.grays.N600}
          onClick={() => {
            setCardToDelete(card)
          }}
        >
          <Delete color={theme.colors.grays.N600} /> Excluir cartão
        </TextButton>
      </Flex>
    ),
    [tableData],
  )

  const handleSubmit = useCallback(
    ({ search, card_type }) => {
      setFilters({
        ...(search && { search }),
        ...(card_type && { card_type: card_type.value }),
      })
    },
    [filters],
  )

  useEffect(() => {
    const fetchCardData = async () => {
      try {
        setIsLoading(true)
        const res = await getCardTypes({ sortBy, pagination, ...filters })
        setTableData(
          res.cards.map(card => ({
            ...card,
            card_type: translateGiftCardType(card.card_type),
            value: card.plans ? card.plans.join(', ') : formatCurrency(Number(card.value)),
            actions: renderTableActions(card),
          })),
        )
        setPagination(res.pagination)
      } catch (e) {
        console.log(e)
        toast.error('Aconteceu um erro ao carregar os dados, por favor tente novamente.')
      } finally {
        setIsLoading(false)
      }
    }

    if (sortBy) {
      fetchCardData()
    }
  }, [sortBy, pagination.currentPage, pagination.pageSize, cardToDelete, filters])

  return (
    <Flex flexDirection='column'>
      <Breadcrumb children={breadcrumbPath} />
      <Flex justifyContent='space-between' alignItems='center' mb={32}>
        <Title as='h3'>Lista de cartões</Title>
        <Button onClick={() => history.push('/staff/prepaid-partners/gift-card/new-card-type')}>Novo cartão</Button>
      </Flex>

      <Formik initialValues={{ search: '', card_type: undefined }} onSubmit={handleSubmit}>
        <Flex as={Form} mb={24}>
          <TextField name='search' label='Pesquisar' placeholder='Pesquisar' mr={24} />
          <SelectField
            name='card_type'
            label='Tipo de cartão'
            placeholder='Tipo de cartão'
            options={[
              { label: 'Valor', value: 'GIFT_CARD' },
              { label: 'Pacotes', value: 'PLANS' },
            ]}
            minWidth={180}
            mr={24}
          />
          <Button variant='secondary'>Filtrar</Button>
        </Flex>
      </Formik>

      <DataListingTable
        data={tableData}
        columns={columns}
        pagination={pagination}
        loading={isLoading}
        fetchData={handleTableDataFetch}
      />

      <DeleteGiftCardModal isOpen={!!cardToDelete} onRequestClose={() => setCardToDelete(null)} card={cardToDelete} />
    </Flex>
  )
}

export default GiftCardList
